/* ==========================================================================
   Estate Marketplace — resident screens.
   Order bulk groceries and market runs (Mile 12 and more), get an invoice,
   pay from the wallet, track delivery, rate it, and see past prices.
   Shown only where the database says shopping is open for this person.
   ========================================================================== */

import { shopService } from "../services/shopService";
import type { ShopCategory, ShopProduct, ShopOrder, ShopItem, ShopEvent, ShopReview, LastPrice, ShopMessage } from "../services/shopService";
import { appStore } from "../state/store";
import { sessionStore } from "../state/session";
import { byId } from "./dom";

interface CartLine { key: string; productId: string; name: string; icon: string; image: string; option: string | null; qty: number; note: string; unit: string }
type View =
  | { name: "home" } | { name: "cat"; slug: string } | { name: "search"; q: string }
  | { name: "cart" } | { name: "orders" } | { name: "order"; id: string } | { name: "prices" };

const CART_KEY = "propervest.shop.cart.v1";
const STEPS: [string, string][] = [
  ["submitted", "Order placed"], ["reviewing", "Pricing"], ["invoiced", "Invoice"], ["paid", "Paid"],
  ["shopping", "Shopping"], ["out_for_delivery", "On the way"], ["delivered", "Delivered"],
];
const STATUS_LABEL: Record<string, string> = {
  submitted: "Order placed", reviewing: "Getting prices", on_hold: "Looking for a vendor", invoiced: "Invoice ready", paid: "Paid", shopping: "Shopping now",
  out_for_delivery: "On the way", delivered: "Delivered", cancelled: "Cancelled",
};
const WINDOWS = ["Any time", "Morning (8am - 12pm)", "Afternoon (12pm - 4pm)", "Evening (4pm - 8pm)"];

const esc = (t: unknown): string => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
const naira = (kobo: number | null | undefined): string => "₦" + Math.round((kobo ?? 0) / 100).toLocaleString("en-NG");
const dt = (iso: string): string => new Date(iso).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Africa/Lagos" });
function day(d: string | null): string {
  if (!d) return "—";
  const [y, m, dd] = d.slice(0, 10).split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}
function daysAgo(d: string): string {
  const [y, m, dd] = d.slice(0, 10).split("-").map(Number) as [number, number, number];
  const n = Math.round((Date.now() - Date.UTC(y, m - 1, dd)) / 86400000);
  return n <= 0 ? "today" : n === 1 ? "yesterday" : `${n} days ago`;
}
const lagosToday = (): string => new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });

export function initShop(opts: { refreshWallet: () => void }): { check(): void } {
  const tabBtn = document.querySelector<HTMLElement>('.tab-btn[data-tab="shop"]');
  const banner = document.getElementById("dashboard-shop-card");
  const bannerBtn = document.getElementById("dashboard-shop-btn");
  const root = byId("shop-root");
  if (!tabBtn) return { check() { /* no shop tab in this page */ } };

  let started = false;
  let categories: ShopCategory[] = [];
  let products: ShopProduct[] = [];
  let prices: LastPrice[] = [];
  let cart: CartLine[] = [];
  const stack: View[] = [];
  let view: View = { name: "home" };
  let toast = "";
  let chatTimer: number | undefined;
  let unread: Record<string, number> = {};
  const stopChat = (): void => { if (chatTimer) { window.clearInterval(chatTimer); chatTimer = undefined; } };
  const unreadTotal = (): number => Object.values(unread).reduce((a, b) => a + b, 0);
  function paintBadge(): void {
    const n = unreadTotal();
    tabBtn!.classList.toggle("has-unread", n > 0);
    const b = document.getElementById("dashboard-shop-btn");
    if (b) b.textContent = n > 0 ? `Shop now (${n} new)` : "Shop now";
  }
  async function refreshUnread(): Promise<void> { unread = await shopService.unreadCounts(); paintBadge(); }

  try { cart = JSON.parse(localStorage.getItem(CART_KEY) || "[]") as CartLine[]; } catch { cart = []; }
  const saveCart = (): void => { try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* ignore */ } };
  const cartCount = (): number => cart.length;

  tabBtn.addEventListener("click", () => { void open(); });
  if (bannerBtn) bannerBtn.addEventListener("click", () => tabBtn.click());

  /** Run after every login: shows or hides the Shop tab and banner. */
  function check(): void {
    started = false; tabBtn!.hidden = true; if (banner) banner.hidden = true;
    categories = []; products = []; prices = [];
    void shopService.available().then((ok) => {
      if (!ok) return;
      started = true;
      tabBtn!.hidden = false;
      if (banner) banner.hidden = false;
      void refreshUnread();
      window.setInterval(() => { if (started) void refreshUnread(); }, 60000);
    });
  }

  async function open(): Promise<void> {
    if (!started) return;
    if (categories.length === 0) {
      root.innerHTML = '<p class="shop-loading">Loading the market…</p>';
      try {
        const c = await shopService.catalog();
        categories = c.categories; products = c.products;
      } catch (e) { root.innerHTML = `<p class="shop-error">Couldn't load Estate Marketplace. ${esc((e as Error).message)}</p>`; return; }
      void shopService.lastPrices().then((p) => { prices = p; }, () => undefined);
    }
    stack.length = 0; view = { name: "home" }; render();
  }

  function go(v: View): void { stack.push(view); view = v; toast = ""; render(); window.scrollTo({ top: 0 }); }
  function back(): void { const v = stack.pop(); view = v ?? { name: "home" }; toast = ""; render(); }
  function priceFor(name: string, option: string | null): LastPrice | undefined {
    return prices.find((p) => p.product_name === name && (p.option_label ?? "") === (option ?? ""));
  }

  function topBar(title: string, canBack: boolean): string {
    return `<div class="shop-top">${canBack ? '<button type="button" class="shop-back" data-act="back" aria-label="Back">‹</button>' : ""}<h2 class="panel-heading shop-title">${esc(title)}</h2></div>`;
  }
  function cartBar(): string {
    if (!cartCount() || view.name === "cart") return "";
    return `<button type="button" class="shop-cartbar" data-act="cart"><span>🧺 View basket</span><b>${cartCount()} item${cartCount() > 1 ? "s" : ""}</b></button>`;
  }
  function img(src: string | null, icon: string, cls: string): string {
    return `<span class="${cls}"><img src="${esc(src ?? "")}" alt="" loading="lazy" onerror="this.remove()"><i aria-hidden="true">${esc(icon)}</i></span>`;
  }

  function render(): void {
    stopChat();
    switch (view.name) {
      case "home": return renderHome();
      case "cat": return renderList(view.slug, "");
      case "search": return renderList("", view.q);
      case "cart": return renderCart();
      case "orders": return void renderOrders();
      case "order": return void renderOrder(view.id);
      case "prices": return renderPrices();
    }
  }

  /* ------------------------------- Home ------------------------------- */
  function renderHome(): void {
    const first = (sessionStore.user?.name ?? "").trim().split(/\s+/)[0] ?? "";
    root.innerHTML = `
      ${topBar("Estate Marketplace", false)}
      <p class="panel-subheading">Bulk groceries and market runs from Mile 12 and more, bought for you and delivered to your door${first ? ", " + esc(first) : ""}.</p>
      <div class="shop-search"><input type="search" id="shop-q" placeholder="Search tomatoes, rice, goat meat, gas…" autocomplete="off" enterkeyhint="search"></div>
      <div class="shop-quick">
        <button type="button" data-act="orders">📦 My orders</button>
        <button type="button" data-act="prices">📈 Price trends</button>
      </div>
      <div class="shop-cats">${categories.map((c) => `
        <button type="button" class="shop-cat" data-cat="${esc(c.slug)}">
          ${img(c.image_url, c.icon, "shop-cat-img")}
          <span class="shop-cat-name">${esc(c.name)}</span><span class="shop-cat-tag">${esc(c.tagline ?? "")}</span>
        </button>`).join("")}</div>
      <div class="shop-how">
        <h3>How it works</h3>
        <ol>
          <li><b>Tell us what you need.</b> Add items and sizes: baskets, bags, full / half / quarter.</li>
          <li><b>We check market prices.</b> Your shopper gets today's prices from the market.</li>
          <li><b>You get an invoice.</b> Goods at market price, plus delivery and a small service fee.</li>
          <li><b>Pay and relax.</b> Pay from your wallet. We shop and deliver. You can track every step.</li>
        </ol>
        <p class="shop-fine">You cover the logistics and service fee. The total is shown on your invoice before you pay anything.</p>
      </div>
      ${cartBar()}`;
  }

  /* ---------------------- Category / search list ---------------------- */
  function renderList(slug: string, q: string): void {
    const cat = categories.find((c) => c.slug === slug);
    const ql = q.trim().toLowerCase();
    const list = products.filter((p) => (cat ? p.category_id === cat.id : p.name.toLowerCase().includes(ql)));
    root.innerHTML = `
      ${topBar(cat ? cat.name : `Results for “${q}”`, true)}
      <div class="shop-search"><input type="search" id="shop-q" value="${esc(q)}" placeholder="Search all products…" autocomplete="off" enterkeyhint="search"></div>
      ${list.length === 0 ? `<p class="shop-empty">Nothing matched. Try another word, or use <b>Anything Else</b> in the list to describe what you need.</p>` : ""}
      <div class="shop-grid">${list.map((p) => `
        <button type="button" class="shop-prod" data-prod="${esc(p.id)}">
          ${img(p.image_url, p.icon, "shop-prod-img")}
          <span class="shop-prod-name">${esc(p.name)}</span>
          <span class="shop-prod-add">+ Add</span>
        </button>`).join("")}</div>
      ${cartBar()}`;
  }

  /* ---------------------------- Product sheet --------------------------- */
  function openProduct(p: ShopProduct): void {
    const opts = p.options.length ? p.options : ["Standard"];
    const wrap = document.createElement("div");
    wrap.className = "shop-sheet-overlay";
    wrap.innerHTML = `<div class="shop-sheet" role="dialog" aria-modal="true">
      <div class="shop-sheet-head">${img(p.image_url, p.icon, "shop-sheet-img")}<div><h3>${esc(p.name)}</h3><p class="shop-fine" id="ps-last"></p></div>
        <button type="button" class="shop-x" aria-label="Close">&times;</button></div>
      <label class="field-label">Choose size / pack</label>
      <div class="shop-opts" id="ps-opts">${opts.map((o, i) => `<button type="button" class="shop-opt${i === 0 ? " is-on" : ""}" data-o="${esc(o)}">${esc(o)}</button>`).join("")}</div>
      <label class="field-label">How many?</label>
      <div class="shop-qty"><button type="button" id="ps-minus" aria-label="Less">−</button><input id="ps-qty" type="number" inputmode="decimal" min="0.5" step="0.5" value="1"><button type="button" id="ps-plus" aria-label="More">+</button></div>
      <label class="field-label">Details (optional)</label>
      <textarea class="text-input" id="ps-note" rows="2" maxlength="300" placeholder="${/meat|goat|ram|beef|pork|chicken|turkey/i.test(p.name) ? "e.g. cut into small pieces, no head, packed in 2 bags" : /tomato|pepper|rodo|tatashe/i.test(p.name) ? "e.g. ripe for stew, not too soft" : "e.g. brand, how ripe, how to cut or pack"}"></textarea>
      <button type="button" class="btn-primary shop-add" id="ps-add">Add to basket</button></div>`;
    document.body.appendChild(wrap);
    let option: string = opts[0] ?? "Standard";
    const qtyEl = wrap.querySelector<HTMLInputElement>("#ps-qty") as HTMLInputElement;
    const lastEl = wrap.querySelector<HTMLElement>("#ps-last") as HTMLElement;
    const close = (): void => wrap.remove();
    const showLast = (): void => {
      const lp = priceFor(p.name, option === "Standard" && !p.options.length ? null : option);
      lastEl.textContent = lp ? `Last bought at ${naira(lp.last_price_kobo)} on ${day(lp.last_date)}` : "";
    };
    showLast();
    wrap.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      if (t === wrap || t.closest(".shop-x")) return close();
      const o = t.closest<HTMLElement>(".shop-opt");
      if (o) { option = o.dataset.o ?? option; wrap.querySelectorAll(".shop-opt").forEach((b) => b.classList.toggle("is-on", b === o)); showLast(); }
    });
    const step = (d: number): void => { qtyEl.value = String(Math.max(0.5, (Number(qtyEl.value) || 1) + d)); };
    (wrap.querySelector("#ps-minus") as HTMLElement).addEventListener("click", () => step(-1));
    (wrap.querySelector("#ps-plus") as HTMLElement).addEventListener("click", () => step(1));
    (wrap.querySelector("#ps-add") as HTMLElement).addEventListener("click", () => {
      const qty = Number(qtyEl.value);
      if (!qty || qty <= 0) { qtyEl.focus(); return; }
      const note = (wrap.querySelector("#ps-note") as HTMLTextAreaElement).value.trim();
      const opt = p.options.length ? option : null;
      const key = `${p.id}|${opt ?? ""}|${note}`;
      const ex = cart.find((l) => l.key === key);
      if (ex) ex.qty += qty; else cart.push({ key, productId: p.id, name: p.name, icon: p.icon, image: p.image_url ?? "", option: opt, qty, note, unit: p.unit });
      saveCart(); close(); render();
    });
  }

  /* ------------------------------- Basket ------------------------------ */
  function renderCart(): void {
    const u = sessionStore.user;
    const minDate = lagosToday();
    root.innerHTML = `
      ${topBar("Your basket", true)}
      ${cart.length === 0 ? '<p class="shop-empty">Your basket is empty. Pick a group and add what you need.</p>' : `
      <div class="shop-lines">${cart.map((l, i) => `
        <div class="shop-line">${img(l.image, l.icon, "shop-line-img")}
          <div class="shop-line-main"><b>${esc(l.name)}</b><span>${l.option ? esc(l.option) : "Standard pack"}</span>${l.note ? `<em>“${esc(l.note)}”</em>` : ""}</div>
          <div class="shop-line-qty"><button type="button" data-q="${i}" data-d="-1">−</button><b>${l.qty}</b><button type="button" data-q="${i}" data-d="1">+</button></div>
          <button type="button" class="shop-line-x" data-rm="${i}" aria-label="Remove">&times;</button></div>`).join("")}</div>
      <div class="shop-form">
        <h3>Delivery details</h3>
        <div class="shop-who"><b>${esc(u?.name ?? "")}</b><span>${esc(u?.estateName ?? "")}${u?.email ? " · " + esc(u.email) : ""}</span></div>
        <label class="field-label" for="sf-phone">Phone number (the shopper will call this)</label>
        <input class="text-input" id="sf-phone" type="tel" inputmode="tel" value="${esc(u?.phone ?? "")}" placeholder="080..." autocomplete="tel">
        <label class="field-label" for="sf-addr">Flat, house number or delivery point</label>
        <input class="text-input" id="sf-addr" type="text" placeholder="e.g. Block 3, Flat 2, near the gate" maxlength="160">
        <div class="shop-two">
          <div><label class="field-label" for="sf-date">Needed by</label><input class="text-input" id="sf-date" type="date" min="${minDate}"></div>
          <div><label class="field-label" for="sf-win">Time of day</label><select class="text-input" id="sf-win">${WINDOWS.map((w) => `<option>${w}</option>`).join("")}</select></div>
        </div>
        <label class="field-label">If something is not available</label>
        <div class="shop-radios">
          <label><input type="radio" name="sf-sub" value="call" checked> Call me first</label>
          <label><input type="radio" name="sf-sub" value="substitute"> Buy the closest alternative</label>
          <label><input type="radio" name="sf-sub" value="skip"> Skip it</label>
        </div>
        <label class="field-label" for="sf-budget">Budget (optional, ₦)</label>
        <input class="text-input" id="sf-budget" type="number" inputmode="numeric" min="0" placeholder="e.g. 80000">
        <label class="field-label" for="sf-notes">Notes for the shopper (optional)</label>
        <textarea class="text-input" id="sf-notes" rows="2" maxlength="600" placeholder="Anything else we should know"></textarea>
        <p class="shop-fine">No payment now. We will check today's market prices and send you an invoice. Goods are at market price. You also cover logistics and a small service fee.</p>
        <p class="shop-error" id="sf-err" hidden></p>
        <button type="button" class="btn-primary shop-add" id="sf-send">Send my order</button>
      </div>`}
      ${cartBar()}`;
  }

  async function sendOrder(): Promise<void> {
    const err = byId("sf-err"); err.hidden = true;
    const btn = byId<HTMLButtonElement>("sf-send");
    const val = (id: string): string => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
    const sub = (document.querySelector<HTMLInputElement>('input[name="sf-sub"]:checked')?.value) ?? "call";
    btn.disabled = true; btn.textContent = "Sending…";
    try {
      const order = await shopService.placeOrder(
        cart.map((l) => ({ product_id: l.productId, option_label: l.option, qty: l.qty, note: l.note })),
        { phone: val("sf-phone"), address_extra: val("sf-addr"), notes: val("sf-notes"), preferred_date: val("sf-date"), window: val("sf-win") === WINDOWS[0] ? "" : val("sf-win"), substitution: sub, budget_naira: val("sf-budget") },
      );
      cart = []; saveCart();
      stack.length = 0; view = { name: "orders" }; toast = `Order ${order.order_no} sent. We will send your invoice soon.`;
      void renderOrders();
    } catch (e) {
      err.textContent = (e as Error).message; err.hidden = false; btn.disabled = false; btn.textContent = "Send my order";
    }
  }

  /* ------------------------------- Orders ------------------------------ */
  async function renderOrders(): Promise<void> {
    root.innerHTML = `${topBar("My orders", true)}<p class="shop-loading">Loading…</p>`;
    try {
      const [orders] = await Promise.all([shopService.listOrders(), refreshUnread()]);
      if (view.name !== "orders") return;
      root.innerHTML = `${topBar("My orders", true)}
        ${toast ? `<p class="shop-toast">${esc(toast)}</p>` : ""}
        ${orders.length === 0 ? '<p class="shop-empty">No orders yet. Your first one is a few taps away.</p>' : ""}
        <div class="shop-orders">${orders.map((o) => `
          <button type="button" class="shop-order" data-order="${esc(o.id)}">
            <div class="shop-order-top"><b>${esc(o.order_no)}</b><span class="shop-chip st-${esc(o.status)}">${esc(STATUS_LABEL[o.status] ?? o.status)}</span></div>
            <div class="shop-order-meta">${esc(dt(o.created_at))} · ${o.shop_order_items?.[0]?.count ?? 0} item(s)${o.total_kobo ? " · " + naira(o.total_kobo) : ""}</div>
            ${unread[o.id] ? `<div class="shop-order-cta">\ud83d\udcac ${unread[o.id]} new message${(unread[o.id] ?? 0) > 1 ? "s" : ""} from us</div>` : ""}
            ${o.status === "invoiced" ? '<div class="shop-order-cta">Invoice ready — tap to review and pay</div>' : ""}
            ${o.status === "delivered" ? '<div class="shop-order-cta">Delivered — tap to rate your order</div>' : ""}
          </button>`).join("")}</div>`;
    } catch (e) { root.innerHTML = `${topBar("My orders", true)}<p class="shop-error">${esc((e as Error).message)}</p>`; }
  }

  function invoiceHtml(o: ShopOrder, items: ShopItem[]): string {
    const rows = items.filter((i) => i.available).map((i) => `<tr><td>${esc(i.product_name)}${i.option_label ? `<br><small>${esc(i.option_label)}</small>` : ""}</td><td>${i.qty}</td><td>${naira(i.unit_price_kobo)}</td><td>${naira(i.line_total_kobo)}</td></tr>`).join("");
    const skipped = items.filter((i) => !i.available).map((i) => esc(i.product_name)).join(", ");
    return `<div class="shop-invoice">
      <div class="inv-head"><div><b>PropertyVest Estate Marketplace</b><br><small>contact@propertyvest.com.ng</small></div><div class="inv-r"><b>INVOICE</b><br>${esc(o.invoice_no ?? "")}<br><small>${o.invoice_issued_at ? esc(dt(o.invoice_issued_at)) : ""}</small></div></div>
      <div class="inv-grid"><div><small>Billed to</small><br><b>${esc(o.customer_name)}</b><br>${esc(o.customer_phone)}<br>${esc(o.delivery_address)}</div>
        <div><small>Prices checked</small><br>${day(o.price_date)}${o.market ? " at " + esc(o.market) : ""}<br><small>Valid until</small><br>${o.invoice_valid_until ? esc(dt(o.invoice_valid_until)) : "—"}</div></div>
      <table><thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Amount</th></tr></thead><tbody>${rows}</tbody></table>
      ${skipped ? `<p><small>Not available: ${skipped}</small></p>` : ""}
      <div class="inv-totals"><div><span>Goods</span><span>${naira(o.subtotal_kobo)}</span></div><div><span>Logistics (delivery)</span><span>${naira(o.logistics_kobo)}</span></div><div><span>Service fee</span><span>${naira(o.service_fee_kobo)}</span></div>${o.other_fee_kobo ? `<div><span>${esc(o.other_fee_label ?? "Other charges")}</span><span>${naira(o.other_fee_kobo)}</span></div>` : ""}<div class="inv-total"><span>Total to pay</span><span>${naira(o.total_kobo)}</span></div></div>
      ${o.invoice_note ? `<p><small>${esc(o.invoice_note)}</small></p>` : ""}</div>`;
  }

  async function renderOrder(id: string): Promise<void> {
    root.innerHTML = `${topBar("Order", true)}<p class="shop-loading">Loading…</p>`;
    let data: { order: ShopOrder; items: ShopItem[]; events: ShopEvent[]; review: ShopReview | null };
    try { data = await shopService.getOrder(id); } catch (e) { root.innerHTML = `${topBar("Order", true)}<p class="shop-error">${esc((e as Error).message)}</p>`; return; }
    if (view.name !== "order" || view.id !== id) return;
    const { order: o, items, events, review } = data;
    const idx = STEPS.findIndex((s) => s[0] === (o.status === "on_hold" ? "reviewing" : o.status));
    const balance = appStore.state.wallet?.balanceKobo ?? 0;
    const expired = o.invoice_valid_until ? new Date(o.invoice_valid_until).getTime() < Date.now() : false;
    const canCancel = ["submitted", "reviewing", "on_hold", "invoiced"].includes(o.status);
    const holdNote = o.status === "on_hold" ? ([...events].reverse().find((e) => e.status === "on_hold")?.note ?? "") : "";

    const stepper = o.status === "cancelled" ? `<div class="shop-cancelled">This order was cancelled.${o.cancelled_reason ? " " + esc(o.cancelled_reason) : ""}</div>` :
      `<ol class="shop-steps">${STEPS.map((s, i) => `<li class="${i < idx ? "done" : i === idx ? "now" : ""}"><span>${i < idx ? "✓" : i + 1}</span><em>${s[1]}</em></li>`).join("")}</ol>`;

    root.innerHTML = `
      ${topBar(o.order_no, true)}
      <div class="shop-status"><span class="shop-chip st-${esc(o.status)}">${esc(STATUS_LABEL[o.status] ?? o.status)}</span><span class="shop-fine">Placed ${esc(dt(o.created_at))}</span></div>
      ${stepper}
      ${o.status === "on_hold" ? `<div class="shop-hold"><b>We are still looking for a vendor</b><p>${esc(holdNote)}</p></div>` : ""}
      ${o.status === "invoiced" ? `
        <div class="shop-pay">
          <h3>Your invoice is ready</h3>
          ${invoiceHtml(o, items)}
          ${expired ? '<p class="shop-error">This invoice has expired because market prices move. Please cancel and place the order again, or contact support for a fresh invoice.</p>' : `
          <p class="shop-fine">Wallet balance: <b>${naira(balance)}</b>${balance < (o.total_kobo ?? 0) ? ' — not enough. Top up your wallet from the Dashboard, then come back.' : ""}</p>
          <p class="shop-error" id="so-err" hidden></p>
          <button type="button" class="btn-primary shop-add" id="so-pay" ${balance < (o.total_kobo ?? 0) ? "disabled" : ""}>Pay ${naira(o.total_kobo)} from wallet</button>`}
          <button type="button" class="shop-link" id="so-print">Print / save invoice</button>
        </div>` : ""}
      <h3 class="shop-h">Your items</h3>
      <div class="shop-lines">${items.map((i) => `
        <div class="shop-line${i.available ? "" : " is-off"}">${img(i.image_url, i.icon ?? "🛒", "shop-line-img")}
          <div class="shop-line-main"><b>${esc(i.product_name)}</b><span>${i.option_label ? esc(i.option_label) : "Standard pack"} × ${i.qty}</span>${i.note ? `<em>“${esc(i.note)}”</em>` : ""}${i.owner_note ? `<em>${esc(i.owner_note)}</em>` : ""}</div>
          <div class="shop-line-price">${o.invoice_issued_at ? (i.available ? naira(i.line_total_kobo) : "Not available") : ""}</div></div>`).join("")}</div>
      ${o.invoice_issued_at && o.status !== "invoiced" ? `<details class="shop-det"><summary>View invoice</summary>${invoiceHtml(o, items)}<button type="button" class="shop-link" id="so-print">Print / save invoice</button></details>` : ""}
      <h3 class="shop-h">Delivery</h3>
      <div class="shop-card"><p>${esc(o.delivery_address)}</p><p class="shop-fine">${o.preferred_date ? "Needed by " + esc(day(o.preferred_date)) : "Any day"}${o.preferred_window ? " · " + esc(o.preferred_window) : ""} · ${esc(o.customer_phone)}</p></div>
      <h3 class="shop-h">Progress</h3>
      <ul class="shop-timeline">${[...events].reverse().map((e) => `<li><b>${esc(STATUS_LABEL[e.status] ?? e.status)}</b><span>${esc(dt(e.created_at))}</span><p>${esc(e.note ?? "")}</p></li>`).join("")}</ul>
      ${chatBlock()}
      ${o.status === "delivered" ? reviewBlock(review) : ""}
      ${canCancel ? '<button type="button" class="shop-link shop-danger" id="so-cancel">Cancel this order</button>' : ""}`;

    const print = document.getElementById("so-print");
    if (print) print.addEventListener("click", () => printInvoice(invoiceHtml(o, items)));
    const pay = document.getElementById("so-pay") as HTMLButtonElement | null;
    if (pay) pay.addEventListener("click", async () => {
      if (!window.confirm(`Pay ${naira(o.total_kobo)} from your wallet for ${o.order_no}?`)) return;
      pay.disabled = true; pay.textContent = "Paying…";
      try { await shopService.payOrder(o.id); opts.refreshWallet(); void renderOrder(o.id); }
      catch (e) { const er = byId("so-err"); er.textContent = (e as Error).message; er.hidden = false; pay.disabled = false; pay.textContent = `Pay ${naira(o.total_kobo)} from wallet`; }
    });
    const cancel = document.getElementById("so-cancel");
    if (cancel) cancel.addEventListener("click", async () => {
      const reason = window.prompt("Why are you cancelling? (optional)");
      if (reason === null) return;
      try { await shopService.cancelOrder(o.id, reason); void renderOrder(o.id); } catch (e) { window.alert((e as Error).message); }
    });
    wireReview(o.id);
    wireChat(o.id);
  }

  function chatBlock(): string {
    return `<h3 class="shop-h">Messages</h3>
      <div class="shop-chat" id="chat-box"><p class="shop-fine">Loading\u2026</p></div>
      <div class="shop-chat-send"><textarea id="chat-in" rows="1" maxlength="1000" placeholder="Ask us anything about this order\u2026"></textarea><button type="button" class="btn-primary" id="chat-send">Send</button></div>
      <p class="shop-error" id="chat-err" hidden></p>`;
  }
  function paintChat(list: ShopMessage[]): void {
    const box = document.getElementById("chat-box"); if (!box) return;
    const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
    box.innerHTML = list.length === 0 ? '<p class="shop-fine">Questions about this order? Message us here. We reply inside the app.</p>' :
      list.map((m) => `<div class="shop-msg ${m.sender_side === "resident" ? "me" : "them"}"><span>${m.sender_side === "resident" ? "You" : "PropertyVest"}</span><p>${esc(m.body)}</p><em>${esc(dt(m.created_at))}</em></div>`).join("");
    if (atBottom) box.scrollTop = box.scrollHeight;
  }
  function wireChat(orderId: string): void {
    const load = async (): Promise<void> => {
      try { const l = await shopService.listMessages(orderId); paintChat(l); if (l.some((m) => m.sender_side === "owner")) { await shopService.markRead(orderId); delete unread[orderId]; paintBadge(); } } catch { /* keep last view */ }
    };
    void load();
    chatTimer = window.setInterval(() => { if (!document.hidden) void load(); }, 15000);
    const send = document.getElementById("chat-send") as HTMLButtonElement | null;
    const input = document.getElementById("chat-in") as HTMLTextAreaElement | null;
    if (!send || !input) return;
    const go = async (): Promise<void> => {
      const text = input.value.trim(); if (!text) return;
      const err = byId("chat-err"); err.hidden = true; send.disabled = true;
      try { await shopService.sendMessage(orderId, text); input.value = ""; await load(); const b = document.getElementById("chat-box"); if (b) b.scrollTop = b.scrollHeight; }
      catch (e) { err.textContent = (e as Error).message; err.hidden = false; }
      send.disabled = false;
    };
    send.addEventListener("click", () => void go());
    input.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void go(); } });
  }

  function stars(name: string): string {
    return `<div class="shop-stars" data-s="${name}">${[1, 2, 3, 4, 5].map((n) => `<button type="button" data-v="${n}" aria-label="${n} star${n > 1 ? "s" : ""}">★</button>`).join("")}</div>`;
  }
  function reviewBlock(r: ShopReview | null): string {
    if (r) return `<h3 class="shop-h">Your review</h3><div class="shop-card"><p class="shop-stars-ro">${"★".repeat(r.rating)}<span>${"★".repeat(5 - r.rating)}</span></p>${r.comment ? `<p>“${esc(r.comment)}”</p>` : ""}${r.owner_reply ? `<p class="shop-reply"><b>PropertyVest replied:</b> ${esc(r.owner_reply)}</p>` : ""}<p class="shop-fine">Thank you for rating your order.</p></div>`;
    return `<h3 class="shop-h">How was your order?</h3><div class="shop-card shop-review">
      <label class="field-label">Overall</label>${stars("rating")}
      <div class="shop-three"><div><label class="field-label">Quality</label>${stars("quality")}</div><div><label class="field-label">Delivery</label>${stars("delivery")}</div><div><label class="field-label">Value</label>${stars("value")}</div></div>
      <label class="field-label" for="rv-rec">How likely are you to recommend us? (0 = never, 10 = definitely)</label>
      <select class="text-input" id="rv-rec"><option value="">Choose…</option>${Array.from({ length: 11 }, (_, i) => `<option value="${i}">${i}</option>`).join("")}</select>
      <label class="field-label" for="rv-text">Comments</label>
      <textarea class="text-input" id="rv-text" rows="3" maxlength="800" placeholder="What went well? What could be better?"></textarea>
      <p class="shop-error" id="rv-err" hidden></p>
      <button type="button" class="btn-primary shop-add" id="rv-send">Submit review</button></div>`;
  }
  function wireReview(orderId: string): void {
    const send = document.getElementById("rv-send") as HTMLButtonElement | null;
    if (!send) return;
    const vals: Record<string, number> = { rating: 0, quality: 0, delivery: 0, value: 0 };
    root.querySelectorAll<HTMLElement>(".shop-stars").forEach((row) => row.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>("button"); if (!b) return;
      const v = Number(b.dataset.v); vals[row.dataset.s ?? ""] = v;
      row.querySelectorAll("button").forEach((x, i) => x.classList.toggle("on", i < v));
    }));
    send.addEventListener("click", async () => {
      const err = byId("rv-err"); err.hidden = true;
      if (!vals.rating) { err.textContent = "Please choose an overall star rating."; err.hidden = false; return; }
      const rec = (document.getElementById("rv-rec") as HTMLSelectElement).value;
      send.disabled = true;
      try {
        await shopService.submitReview({ orderId, rating: vals.rating ?? 0, quality: vals.quality ?? 0, delivery: vals.delivery ?? 0, value: vals.value ?? 0, recommend: rec === "" ? null : Number(rec), comment: (document.getElementById("rv-text") as HTMLTextAreaElement).value.trim() });
        void renderOrder(orderId);
      } catch (e) { err.textContent = (e as Error).message; err.hidden = false; send.disabled = false; }
    });
  }
  function printInvoice(html: string): void {
    const w = window.open("", "_blank");
    if (!w) { window.alert("Please allow pop-ups to print your invoice."); return; }
    w.document.write(`<!doctype html><title>Invoice</title><style>body{font:14px system-ui,sans-serif;padding:24px;color:#111}table{width:100%;border-collapse:collapse}th,td{text-align:left;padding:8px;border-bottom:1px solid #ddd}.inv-head,.inv-grid{display:flex;justify-content:space-between;gap:24px;margin-bottom:18px}.inv-totals div{display:flex;justify-content:space-between;padding:4px 0}.inv-total{font-weight:700;border-top:2px solid #111;margin-top:6px;padding-top:8px!important}</style>${html}<script>onload=()=>print()<\/script>`);
    w.document.close();
  }

  /* ---------------------------- Price trends --------------------------- */
  function renderPrices(q = ""): void {
    const ql = q.toLowerCase();
    const list = prices.filter((p) => p.product_name.toLowerCase().includes(ql));
    root.innerHTML = `${topBar("Price trends", true)}
      <p class="panel-subheading">What things cost on past completed orders, with the date each price was checked at the market. Prices move, so check the date.</p>
      <div class="shop-search"><input type="search" id="shop-pq" value="${esc(q)}" placeholder="Search products…" autocomplete="off"></div>
      ${list.length === 0 ? '<p class="shop-empty">No prices yet. They appear here once orders are delivered.</p>' : ""}
      <div class="shop-prices">${list.map((p, i) => {
        const ch = p.change_pct;
        return `<button type="button" class="shop-price" data-pi="${prices.indexOf(p)}">
          <div class="shop-price-top"><b>${esc(p.product_name)}</b><span>${esc(p.option_label ?? "Standard pack")}</span></div>
          <div class="shop-price-main"><strong>${naira(p.last_price_kobo)}</strong>
            ${ch == null ? "" : `<span class="shop-delta ${ch > 0 ? "up" : ch < 0 ? "down" : ""}">${ch > 0 ? "▲" : ch < 0 ? "▼" : "="} ${Math.abs(ch)}%</span>`}</div>
          <div class="shop-price-date"><span>Last price date</span><b>${esc(day(p.last_date))}</b><em>${esc(daysAgo(p.last_date))}</em></div>
          ${p.previous_price_kobo ? `<div class="shop-fine">Before: ${naira(p.previous_price_kobo)} on ${esc(day(p.previous_date))}</div>` : ""}
          <span class="sr-only">${i}</span></button>`;
      }).join("")}</div>
      ${cartBar()}`;
    const input = document.getElementById("shop-pq") as HTMLInputElement | null;
    if (input) { input.addEventListener("input", () => { const pos = input.selectionStart; renderPrices(input.value); const n = document.getElementById("shop-pq") as HTMLInputElement; n.focus(); n.setSelectionRange(pos, pos); }); }
  }
  async function openTrend(p: LastPrice): Promise<void> {
    const wrap = document.createElement("div");
    wrap.className = "shop-sheet-overlay";
    wrap.innerHTML = `<div class="shop-sheet"><div class="shop-sheet-head"><div><h3>${esc(p.product_name)}</h3><p class="shop-fine">${esc(p.option_label ?? "Standard pack")}</p></div><button type="button" class="shop-x" aria-label="Close">&times;</button></div><div id="tr-body"><p class="shop-loading">Loading…</p></div></div>`;
    document.body.appendChild(wrap);
    wrap.addEventListener("click", (e) => { const t = e.target as HTMLElement; if (t === wrap || t.closest(".shop-x")) wrap.remove(); });
    try {
      const pts = await shopService.priceTrend(p.product_name, p.option_label);
      const max = Math.max(...pts.map((x) => x.unit_price_kobo));
      const min = Math.min(...pts.map((x) => x.unit_price_kobo));
      const chrono = [...pts].reverse();
      (wrap.querySelector("#tr-body") as HTMLElement).innerHTML = `
        ${pts.length > 1 ? `<div class="shop-spark">${chrono.map((x) => `<i style="height:${max === min ? 60 : 20 + ((x.unit_price_kobo - min) / (max - min)) * 80}%" title="${esc(day(x.price_date))}: ${naira(x.unit_price_kobo)}"></i>`).join("")}</div>
        <p class="shop-fine">Lowest ${naira(min)} · Highest ${naira(max)}</p>` : ""}
        <ul class="shop-trend">${pts.map((x) => `<li><b>${naira(x.unit_price_kobo)}</b><span>${esc(day(x.price_date))}${x.market ? " · " + esc(x.market) : ""}</span></li>`).join("")}</ul>`;
    } catch (e) { (wrap.querySelector("#tr-body") as HTMLElement).innerHTML = `<p class="shop-error">${esc((e as Error).message)}</p>`; }
  }

  /* --------------------------- One click handler ------------------------ */
  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const act = t.closest<HTMLElement>("[data-act]")?.dataset.act;
    if (act === "back") return back();
    if (act === "cart") return go({ name: "cart" });
    if (act === "orders") return go({ name: "orders" });
    if (act === "prices") return go({ name: "prices" });
    const cat = t.closest<HTMLElement>("[data-cat]"); if (cat) return go({ name: "cat", slug: cat.dataset.cat ?? "" });
    const prod = t.closest<HTMLElement>("[data-prod]"); if (prod) { const p = products.find((x) => x.id === prod.dataset.prod); if (p) openProduct(p); return; }
    const ord = t.closest<HTMLElement>("[data-order]"); if (ord) return go({ name: "order", id: ord.dataset.order ?? "" });
    const pr = t.closest<HTMLElement>("[data-pi]"); if (pr) { const p = prices[Number(pr.dataset.pi)]; if (p) void openTrend(p); return; }
    const q = t.closest<HTMLElement>("[data-q]");
    if (q) { const l = cart[Number(q.dataset.q)]; if (l) { l.qty = Math.max(0.5, l.qty + Number(q.dataset.d)); saveCart(); renderCart(); } return; }
    const rm = t.closest<HTMLElement>("[data-rm]");
    if (rm) { cart.splice(Number(rm.dataset.rm), 1); saveCart(); renderCart(); return; }
    if (t.closest("#sf-send")) void sendOrder();
  });
  root.addEventListener("keydown", (e) => {
    const t = e.target as HTMLInputElement;
    if (e.key === "Enter" && t.id === "shop-q" && t.value.trim()) go({ name: "search", q: t.value.trim() });
  });
  return { check };
}
